import React, { useRef, useState, useMemo } from "react";
import { DA_Container } from "@danishagro/shared/src/components/atoms/Container/Container.component";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { DA_ArrowButton, DA_arrowDirection } from "@molecules/ArrowButton/ArrowButton.component";
import { DA_ImageLink, DA_ImageLinkProps } from "@molecules/ImageLink/ImageLink.component";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Swiper as SwiperClass, SwiperOptions } from "swiper/types";
import "@danishagro/shared/src/helpers/swiperInitialization.helper";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { ImageConfig } from "@danishagro/shared/src/content/imageConfigs/imageConfig.enum";
import S from "./CategorySliderV1.module.scss";

export interface V1Props {
    title: string;
}

export const V1: React.FC<V1Props> = ({ title }) => {
    const { siteVariant } = useAppData();
    const [slidesPerView, setSlidesPerView] = useState(0);
    const [showPreviousButton, setShowPreviousButton] = useState(false);
    const [showNextButton, setShowNextButton] = useState(false);
    const sliderRef = useRef<SwiperClass>(null);
    const { getDictionaryString } = useTranslations();
    const isVilofarm = siteVariant.alias === "vilofarm";

    //  Automatically fetch category banner data
    const items: DA_ImageLinkProps[] = useMemo(() => {
        if (!globalThis.categoryBannerObj || globalThis.categoryBannerObj.length === 0) {
            console.log("No category banner data found in globalThis.categoryBannerObj");
            return [];
        }

        return globalThis.categoryBannerObj.map((category) => ({
            title: category.name,
            url: category.path,
            imageAltText: category.name,
            image: category.imageUrl ? `${category.imageUrl}/${ImageConfig.CategoryImage}` : "",
        }));
    }, []);

    const onSlideChange = (swiper: SwiperClass) => {
        setShowPreviousButton(swiper.realIndex > 0);
        setShowNextButton(swiper.realIndex < items.length - slidesPerView);
    };

    const onBreakPointChange = (swiper: SwiperClass, breakpoint: SwiperOptions) => {
        setSlidesPerView(breakpoint.slidesPerView as number);
        swiper.slideTo(0);
        setShowPreviousButton(false);
        setShowNextButton(items.length > (breakpoint.slidesPerView as number));
    };

    return items.length > 0 ? (
        <DA_Container noOverflow>
            <div className={S.sliderTopWrapper}>
                <DA_Title h2>{title}</DA_Title>
            </div>

            <div className={S.swiperWrapper}>
                <div className={S.swiperWrapperInner}>
                    <div className={S.navControl}>
                        {items.length >= slidesPerView && (
                            <>
                                <div
                                    className={cn(
                                        S.navigationWrapper,
                                        S.previous,
                                        showPreviousButton && S.visible
                                    )}
                                >
                                    <DA_ArrowButton
                                        onCLick={() => sliderRef.current?.slidePrev()}
                                        iconDirection={DA_arrowDirection.Left}
                                        className={S.buttonNav}
                                        disabled={!showPreviousButton}
                                        small
                                        title={getDictionaryString("accessibilityButtonPrev")}
                                        isGhost
                                    />
                                </div>

                                <div
                                    className={cn(
                                        S.navigationWrapper,
                                        S.next,
                                        showNextButton && S.visible
                                    )}
                                >
                                    <DA_ArrowButton
                                        onCLick={() => sliderRef.current?.slideNext()}
                                        className={S.buttonNav}
                                        disabled={!showNextButton}
                                        small
                                        title={getDictionaryString("accessibilityButtonNext")}
                                        isGhost={true}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <Swiper
                        modules={[Pagination]}
                        threshold={10}
                        slidesPerView={2}
                        spaceBetween={10}
                        onSlideChange={onSlideChange}
                        onReachBeginning={() => setShowPreviousButton(false)}
                        onReachEnd={() => setShowNextButton(false)}
                        onBreakpoint={onBreakPointChange}
                        onSwiper={(swiper) => (sliderRef.current = swiper)}
                        pagination={{ type: "progressbar" }}
                        breakpoints={{
                            512: { slidesPerView: 2.2, spaceBetween: 22 },
                            768: { slidesPerView: 3, spaceBetween: 22 },
                            992: { slidesPerView: isVilofarm ? 3 : 4, spaceBetween: 22 },
                        }}
                    >
                        {items.map((item, index) => (
                            <SwiperSlide key={index}>
                                <DA_ImageLink {...item} limitedHeight={isVilofarm} />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </DA_Container>
    ) : null;
};
